import { redirect } from "@tanstack/react-router";
import { useOrgStore } from "@/stores";
import type { OrgRole } from "@/types/organization.types";
import { showError } from "@/lib/toast";

// Org role hierarchy — higher index = more privilege.
// Mirrors backend `middleware.IsOrgAdmin` (owner+admin pass) so route gates
// stay consistent with API enforcement.
const ROLE_RANK: Record<OrgRole, number> = {
  member: 0,
  admin: 1,
  owner: 2,
};

// Org store starts empty on every cold/hot reload. Awaiting hydrate before
// gating prevents bouncing the user to /select-org when the cookie still
// holds a valid org id.
async function ensureOrgHydrated(): Promise<void> {
  const store = useOrgStore.getState();
  if (store.isInitialized) return;
  if (store.isLoading) {
    await new Promise<void>((resolve) => {
      const unsubscribe = useOrgStore.subscribe((state) => {
        if (state.isInitialized || !state.isLoading) {
          unsubscribe();
          resolve();
        }
      });
      const latest = useOrgStore.getState();
      if (latest.isInitialized || !latest.isLoading) {
        unsubscribe();
        resolve();
      }
    });
    return;
  }
  await store.hydrate();
}

// Role gate for privileged routes. Passes when the requester's role is the
// required level OR HIGHER. Callers should have already passed requireAuth +
// requireOrg, but we still await hydrate defensively in case of direct deep
// links.
export async function requireRole(required: OrgRole): Promise<void> {
  await ensureOrgHydrated();
  const role = useOrgStore.getState().currentOrg?.myRole ?? null;
  if (role === null || ROLE_RANK[role] < ROLE_RANK[required]) {
    showError("You are not authorised to view this page");
    throw redirect({ to: "/" });
  }
}

// Throws if user has no current org selected → bounce to /select-org.
// Used inside _admin layout beforeLoad.
export async function requireOrg(): Promise<void> {
  await ensureOrgHydrated();
  if (!useOrgStore.getState().currentOrg) {
    throw redirect({ to: "/select-org" });
  }
}
